
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Mail, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import LazyImage from '@/components/LazyImage';
import { useTranslation } from 'react-i18next';

const Header = () => {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const handleLanguageChange = (e) => {
    e.preventDefault();
    const newLang = i18n.language === 'es' ? 'en' : 'es';
    i18n.changeLanguage(newLang);
  };

  const navigation = [
    { name: t('nav.home'), href: '/' },
    { name: t('nav.products'), href: '/productos' },
    { name: t('nav.about'), href: '/nosotros' },
    { name: t('nav.contact'), href: '/contacto' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Contact Strip */}
      <div className="contact-strip py-2 px-4 text-sm border-b-0">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row md:justify-between">
            <div className="flex flex-col space-y-1 md:flex-row md:items-center md:space-x-6 md:space-y-0">
              <div className="flex items-start md:items-center space-x-1">
                <Phone className="h-4 w-4 plasa-red flex-shrink-0 mt-0.5 md:mt-0" />
                <div className="whitespace-nowrap text-left">(33) 3821 7408 / (33) 1871 5068</div>
              </div>
              <div className="flex items-start md:items-center space-x-1">
                <Mail className="h-4 w-4 plasa-red flex-shrink-0 mt-0.5 md:mt-0" />
                <a href="mailto:plasapcg@plasa.com.mx" className="hover:text-plasa-red text-left">plasapcg@plasa.com.mx</a>
              </div>
            </div>
            <div className="text-xs text-gray-600 mt-1 md:mt-0 text-left md:text-right">
              {t('nav.hours')}
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-white sticky top-0 z-[1000] shadow-md">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <LazyImage
                src="/images/home/logo.png"
                alt="PLASA Logo"
                className="h-12 w-auto object-contain"
              />
              <div className="ml-2">
                <div className="text-sm md:text-lg font-bold text-plasa-red tracking-wide whitespace-nowrap">
                  {t('nav.welcome')}
                </div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`relative px-3 py-2 text-sm font-medium transition-colors duration-200 ${isActive(item.href)
                    ? 'plasa-red'
                    : 'text-gray-700 hover:text-red-600'
                    }`}
                >
                  {item.name}
                  {isActive(item.href) && (
                    <motion.div
                      className="absolute bottom-0 left-0 right-0 h-0.5 plasa-red-bg"
                      layoutId="activeTab"
                      initial={false}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  )}
                </Link>
              ))}

              {/* Language Switcher Desktop */}
              <button
                onClick={handleLanguageChange}
                className="flex items-center space-x-1 text-sm font-medium text-gray-700 hover:text-red-600 transition-colors border-l pl-4 border-gray-200"
              >
                <Globe className="h-4 w-4" />
                <span>{i18n.language === 'es' ? 'EN' : 'ES'}</span>
              </button>
            </nav>

            {/* Mobile Actions */}
            <div className="flex items-center space-x-4 lg:hidden">


              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t overflow-hidden"
            >
              <div className="container mx-auto px-4 py-4">
                <nav className="flex flex-col space-y-4">
                  {navigation.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className={`px-3 py-2 text-base font-medium transition-colors duration-200 ${isActive(item.href)
                        ? 'plasa-red bg-red-50 rounded-lg'
                        : 'text-gray-700 hover:text-red-600 hover:bg-gray-50 rounded-lg'
                        }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  ))}

                  <div className="pt-4 mt-2 border-t border-gray-100">
                    <button
                      onClick={(e) => {
                        handleLanguageChange(e);
                        setIsMenuOpen(false);
                      }}
                      className="flex items-center space-x-2 px-3 py-2 text-base font-medium text-gray-700 hover:text-red-600 hover:bg-gray-50 rounded-lg w-full"
                    >
                      <Globe className="h-5 w-5" />
                      <span>{i18n.language === 'es' ? 'Switch to English' : 'Cambiar a Español'}</span>
                    </button>
                  </div>
                </nav>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

export default Header;
